import { useGridApiRef } from '@mui/x-data-grid-pro';
import React, { ReactElement, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
    MlTrainingActionsBar,
    MlTrainingActionsBarType,
    SelectionCountHandle,
} from 'app/components/ml-training-actions-bar';
import { TrainingModel } from 'app/model/machine-learning/training';
import { MidTuple } from 'app/model/mid-tuple';
import { trainingService } from 'app/services/machine-learning/training.service';
import { WithEsId } from 'app/type/with-es-id';

import { FilterVersion } from './ml-training-filter';
import { MemoizedTupleResultsTable, TupleTableProps } from './tuple-results-table';

type MlTupleResultsTableProps = Omit<
    TupleTableProps,
    'gridApiRef' | 'MlTrainingActionsBarSlot' | 'onRowSelectionModelChange'
> & {
    primaryStore?: MlTrainingActionsBarType['primaryStore'];
    mlTrainingFilterVersion?: FilterVersion;
};
const emptyMarkedTuples = new Map<string, TrainingModel>([]);
const MlTupleResultsTable = ({
    primaryStore,
    mlTrainingFilterVersion,
    tuples: tuplesProp,
    ...props
}: MlTupleResultsTableProps): ReactElement => {
    const gridApiRef = useGridApiRef();
    const [markedTuples, setMarkedTuples] = useState<Map<string, TrainingModel>>(emptyMarkedTuples);

    useEffect(() => {
        setMarkedTuples(emptyMarkedTuples);
    }, [tuplesProp]);

    const tuples = useMemo(() => {
        return 0 < markedTuples.size
            ? tuplesProp.map((tupleArr) => {
                  return tupleArr.map((tuple) => {
                      const markedTuple = markedTuples.get(tuple.mtid);

                      return markedTuple
                          ? {
                                ...tuple,
                                mlType: markedTuple.type,
                                mlTagNegative: markedTuple.isNegative,
                                mlTagId: markedTuple.store ? markedTuple.store.id : '',
                                mlTagName: markedTuple.store ? markedTuple.store.name : 'NONE',
                            }
                          : tuple;
                  });
              })
            : tuplesProp;
    }, [markedTuples, tuplesProp]);

    // eslint-disable-next-line no-null/no-null
    const mlTrainingActionsBarRef = useRef<SelectionCountHandle>(null);

    const onRowSelectionModelChangeHandler = useCallback<NonNullable<TupleTableProps['onRowSelectionModelChange']>>(
        (newModel) => {
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            mlTrainingActionsBarRef.current!.setSelectionCount(newModel.length);
        },
        [],
    );
    const onApplyActionsHandler = useCallback<MlTrainingActionsBarType['onApplyActions']>(
        (store, positive) => {
            const selectedRows = gridApiRef.current.getSelectedRows();

            trainingService
                .mark({
                    store,
                    isNegative: !positive,
                    mtidArray: Array.from(selectedRows as Map<string, WithEsId<MidTuple>>, ([, { mtid }]) => mtid),
                })
                .subscribe((resp) => {
                    setMarkedTuples((previousMarkedTuples) => {
                        return new Map([
                            ...previousMarkedTuples,
                            ...resp.map(
                                (trainingModel) => [trainingModel.mtid, trainingModel] as [string, TrainingModel],
                            ),
                        ]);
                    });

                    gridApiRef.current.setRowSelectionModel([]);
                });
        },
        [gridApiRef],
    );

    const onClearMarkHandler = useCallback<MlTrainingActionsBarType['onClearMark']>(() => {
        const selectedRows = gridApiRef.current.getSelectedRows();
        const mtids = Array.from(selectedRows as Map<string, WithEsId<MidTuple>>, ([, { mtid }]) => mtid);

        trainingService.clear(mtids).subscribe(() => {
            setMarkedTuples((previousMarkedTuples) => {
                const updated = new Map(previousMarkedTuples);
                mtids.forEach((mtid) => updated.delete(mtid));

                return updated;
            });

            gridApiRef.current.setRowSelectionModel([]);
        });
    }, [gridApiRef]);

    return (
        <MemoizedTupleResultsTable
            {...props}
            tuples={tuples}
            primaryStore={primaryStore}
            mlTrainingFilterVersion={mlTrainingFilterVersion}
            hideDefaultHeader
            gridApiRef={gridApiRef}
            onRowSelectionModelChange={onRowSelectionModelChangeHandler}
            MlTrainingActionsBarSlot={
                <MlTrainingActionsBar
                    sx={{ mb: 2 }}
                    ref={mlTrainingActionsBarRef}
                    primaryStore={primaryStore}
                    onClear={() => {
                        gridApiRef.current.setRowSelectionModel([]);
                    }}
                    onApplyActions={onApplyActionsHandler}
                    onClearMark={onClearMarkHandler}
                />
            }
        />
    );
};

const MemoizedMlTupleResultsTable = React.memo(MlTupleResultsTable);

export { MemoizedMlTupleResultsTable as MlTupleResultsTable };
